import { Language } from '@nx-smartmonkey/shared/domain';
import queryString from 'query-string';

import { Organization } from '../domain/organization/Organization';
import { OrganizationUser } from '../domain/organization/OrganizationUser';
import { OrganizationUserRoles } from '../domain/organization/OrganizationUserRoles';
import { OrganizationResources } from '../domain/resources/OrganizationResources';
import { BillingPeriod, PlanType } from '../domain/subscription/Subscription';
import { User } from '../domain/user/User';
import { organizationRepository } from '../repositories/organization';
import { OrganizationRepository } from '../repositories/organization/OrganizationRepository';
import { sessionRepository } from '../repositories/session';
import { SessionRepository } from '../repositories/session/SessionRepository';
import { userRepository } from '../repositories/user';
import { UserRepository } from '../repositories/user/UserRepository';
import eventsPanel from '../services/events';
import { intercom } from '../services/intercom';

export class OrganizationService {
  constructor(
    private organizationRepository: OrganizationRepository,
    private sessionRepository: SessionRepository,
    private userRepository: UserRepository
  ) {}

  async register({
    firstName,
    lastName,
    email,
    password,
    phone,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone: string;
  }): Promise<Organization> {
    const registerUtmInfo = {
      utm_campaign: ``,
      utm_content: ``,
      utm_source: ``,
      utm_term: ``,
      utm_medium: ``,
      utm_id: ``,
      utm_gclid: ``,
    };

    const gtmCampaignCookie = this.sessionRepository.getCookie(`__gtm_campaign_url`);
    if (gtmCampaignCookie) {
      const utmUrl = decodeURIComponent(gtmCampaignCookie);
      const { query } = queryString.parseUrl(utmUrl);
      registerUtmInfo[`utm_campaign`] = (query[`utm_campaign`] ?? ``) as string;
      registerUtmInfo[`utm_content`] = (query[`utm_content`] ?? ``) as string;
      registerUtmInfo[`utm_source`] = (query[`utm_source`] ?? ``) as string;
      registerUtmInfo[`utm_term`] = (query[`utm_term`] ?? ``) as string;
      registerUtmInfo[`utm_medium`] = (query[`utm_medium`] ?? ``) as string;
      registerUtmInfo[`utm_id`] = (query[`utm_id`] ?? ``) as string;
      registerUtmInfo[`utm_gclid`] = (query[`utm_gclid`] ?? ``) as string;
    }

    const country = await this.sessionRepository.getCountry();

    const language = this.sessionRepository.getLanguage();

    const organization = await this.organizationRepository.register({
      firstName,
      lastName,
      email,
      password,
      phone,
      country,
      language: language ?? Language.getDefaultLanguage(),
      registerUtmInfo,
    });

    this.userRepository.setUserLocationByCountry(country);

    eventsPanel.registerOrganization({
      firstName,
      lastName,
      email,
      phone,
      organizationId: organization.id,
      ownerId: organization.owner_id,
      createdAt: organization.created_at,
      registerUtmInfo,
      language: language ?? Language.getDefaultLanguage(),
    });

    return organization;
  }

  async fetch(loggedUser: User): Promise<Organization> {
    const organization = await this.organizationRepository.fetch();

    intercom.setUser({
      company: {
        company_id: organization.id,
        name: organization.label,
        status: organization.subscription.status,
        plan: organization.subscription.plan,
        vehicles: organization.subscription.vehicles,
        created_at: organization.created_at,
      },
      numOfVehicles: organization.num_of_vehicles,
    });

    eventsPanel.completeLogin({
      planType: organization.subscription.status === `active` ? organization.subscription.plan : undefined,
    });

    this.userRepository.setUserLocationByCountry(organization.country_code);

    return organization;
  }

  async save({ organization }: { organization: Organization }): Promise<Organization> {
    const updatedOrganization = await this.organizationRepository.save(organization);
    return updatedOrganization;
  }

  fetchAvatar({ organization }: { organization: Organization }): Promise<Organization> {
    // FEM AQUI LO DE TRACTAR LA IMATGE???
    return this.organizationRepository.fetchAvatar({ organization });
  }

  async createAvatar({
    organization,
    fileName,
    mimeType,
    file,
  }: {
    organization: Organization;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<Organization> {
    const updatedOrganization = await this.organizationRepository.createAvatar({
      organization,
      fileName,
      mimeType,
      file,
    });
    return updatedOrganization;
  }

  async deleteAvatar({ organization }: { organization: Organization }): Promise<Organization> {
    const updatedOrganization = await this.organizationRepository.deleteAvatar({ organization });
    return updatedOrganization;
  }

  async retrieveUsers(): Promise<OrganizationUser[]> {
    return this.organizationRepository.retrieveUsers();
  }

  async createUser({
    email,
    firstName,
    lastName,
    role,
    language,
    projects,
  }: {
    email: string;
    firstName: string;
    lastName: string;
    role: OrganizationUserRoles;
    language: Language;
    projects: string[];
  }): Promise<void> {
    await this.organizationRepository.createUser({
      email,
      firstName,
      lastName,
      role,
      language,
      projects,
    });
  }

  async deleteUser({
    organizationUser,
    organization,
  }: {
    organizationUser: OrganizationUser;
    organization: Organization;
  }): Promise<Organization> {
    const updatedOrganization = await this.organizationRepository.deleteUser({
      organizationUser,
      organization,
    });
    return updatedOrganization;
  }

  async editUserRole({
    organizationUser,
    organization,
  }: {
    organizationUser: OrganizationUser;
    organization: Organization;
  }): Promise<Organization> {
    const updatedOrganization = await this.organizationRepository.editUser({ organizationUser, organization });
    return updatedOrganization;
  }

  async resendUserInvitation({
    userId,
    organization,
  }: {
    userId: string;
    organization: Organization;
  }): Promise<Organization> {
    return this.organizationRepository.resendUserInvitation({ userId, organization });
  }

  async restoreUser({ userId }: { userId: string }): Promise<void> {
    await this.organizationRepository.restoreUser({
      userId,
    });
  }

  async fetchUserInvitation({ token }: { token: string }): Promise<{
    language: Language;
    email: string;
    firstName: string;
    lastName: string;
    organizationLabel: string;
  }> {
    return this.organizationRepository.fetchUserInvitation({ token });
  }

  async acceptUserInvitation({
    token,
    language,
    password,
    firstName,
    lastName,
    phone,
  }: {
    token: string;
    language: Language;
    password: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<void> {
    return this.organizationRepository.acceptUserInvitation({ token, language, password, firstName, lastName, phone });
  }

  async createPaymentLink({
    redirectUrl,
    plan,
    billingPeriod,
  }: {
    redirectUrl: string;
    plan: PlanType;
    billingPeriod: BillingPeriod;
  }): Promise<string> {
    return this.organizationRepository.createPaymentLink({
      redirectUrl,
      plan,
      billingPeriod,
    });
  }

  async retrieveResources(): Promise<OrganizationResources> {
    return this.organizationRepository.retrieveResources();
  }
}

export const organizationService = new OrganizationService(organizationRepository, sessionRepository, userRepository);
